import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import InstallPrompt from "@/components/InstallPrompt";
import IosInstallCard from "@/components/IosInstallCard";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Profiles from "@/pages/Profiles";
import Board from "@/pages/Board";
import Settings from "@/pages/Settings";
import Report from "@/pages/Report";
import FirstThen from "@/pages/FirstThen";
import ChoiceBoard from "@/pages/ChoiceBoard";
import Coach from "@/pages/Coach";
import InviteRedeem from "@/pages/InviteRedeem";
import ReferRedeem from "@/pages/ReferRedeem";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import About from "@/pages/About";
import Community from "@/pages/Community";
import Press from "@/pages/Press";
import Download from "@/pages/Download";
import Pricing from "@/pages/Pricing";  // the canonical ladder (CANON block 2)
import PublicHeader from "@/components/PublicHeader";
import SiteFooter from "@/components/SiteFooter";
import AboutUs from "@/pages/AboutUs";
import Contact from "@/pages/Contact";
import Security from "@/pages/Security";
import DataProtection from "@/pages/DataProtection";
import ReadyBoards from "@/pages/ReadyBoards";
import Professionals from "@/pages/Professionals";
import NotFound from "@/pages/NotFound";
import BillingSuccess from "@/pages/BillingSuccess";
import Therapists from "@/pages/Therapists";
import TherapistDetail from "@/pages/TherapistDetail";
import TherapistApplication from "@/pages/TherapistApplication";
import FamilyProfile from "@/pages/FamilyProfile";
import FamilyProfileEditor from "@/pages/FamilyProfileEditor";
import AdminVerify from "@/pages/AdminVerify";
import AdminAuditLog from "@/pages/AdminAuditLog";
import AdminGrant from "@/pages/AdminGrant";
import AdminSecurity from "@/pages/AdminSecurity";
import AdminRegion from "@/pages/AdminRegion";
import AdminFunnel from "@/pages/AdminFunnel";
import AdminDoctorCodes from "@/pages/AdminDoctorCodes";
import AdminPilot from "@/pages/AdminPilot";
import { BlogIndex, BlogPost } from "@/pages/Blog";
import ShareImport from "@/pages/ShareImport";
import AuthCallback from "@/pages/AuthCallback";
import TherapistDashboard from "@/pages/TherapistDashboard";
import DemoSafety from "@/pages/DemoSafety";
import ParentalConsentVerify from "@/pages/ParentalConsentVerify";
import PrivacyMe from "@/pages/PrivacyMe";
import HelperAccept from "@/pages/HelperAccept";
import BuiltInADay from "@/pages/BuiltInADay";
import Waitlist from "@/pages/Waitlist";
import WhatsNew from "@/pages/WhatsNew";
import PairChildDevice from "@/pages/PairChildDevice";
import AdminChildDevices from "@/pages/AdminChildDevices";
import AdminMedia from "@/pages/AdminMedia";
import ManageBoard from "@/pages/ManageBoard";
import PickBoard from "@/pages/PickBoard";
import CreateBoardWizard from "@/pages/CreateBoardWizard";
import NotificationsPage from "@/pages/NotificationsPage";
import PrivacyPromisePage from "@/pages/PrivacyPromisePage";
import DemoBoardPage from "@/pages/DemoBoardPage";
import LivingTileStudio from "@/pages/LivingTileStudio";
import DemoStaged from "@/pages/DemoStaged";
import MyProfile from "@/pages/MyProfile";
import ComingSoonPage from "@/pages/ComingSoonPage";
import TopNav from "@/components/TopNav";
import ChildAvatarStrip from "@/components/ChildAvatarStrip";
import TickerBanner from "@/components/TickerBanner";
import DemoBanner from "@/components/DemoBanner";
import PublicTopBar from "@/components/PublicTopBar";
import TierUpgradeModal from "@/components/TierUpgradeModal";
import ChildDeviceShell from "@/components/ChildDeviceShell";
import PWAUpdateBanner from "@/components/PWAUpdateBanner";
import { isChildDevice } from "@/lib/childDevice";
import { TIER_UPGRADE_EVENT } from "@/lib/api";
import { useEffect, useState } from "react";

function TierUpgradeListener() {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    const onEvt = (e) => {
      // Only set if not already showing — first 402 wins, prevent stacking
      // when a page issues several parallel mutations on Starter.
      setDetail((cur) => cur || e.detail);
    };
    window.addEventListener(TIER_UPGRADE_EVENT, onEvt);
    return () => window.removeEventListener(TIER_UPGRADE_EVENT, onEvt);
  }, []);
  if (!detail) return null;
  return <TierUpgradeModal detail={detail} onClose={() => setDetail(null)} />;
}

function AppRouter() {
  const location = useLocation();
  // Detect Emergent OAuth redirect synchronously during render. REMINDER: DO NOT
  // HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const hash = location.hash || window.location.hash || "";
  if (hash.includes("session_id=")) return <AuthCallback />;

  // If this device is paired as a child tablet, swap in the locked router.
  // The parent must call /child-device/{id}/unpair from another device to
  // exit — the tablet itself can't escape. Allow /pair-device through so a
  // tablet that lost its marker (e.g. cache wiped) can re-pair.
  if (isChildDevice() && location.pathname !== "/pair-device") {
    return <ChildDeviceShell />;
  }

  // Public marketing surface — hamburger menu shows here. Auth'd app surface
  // (board / settings / admin / billing) skips it to keep the UI clean
  // for users already in the product.
  const PUBLIC_BANNER_PATHS = new Set([
    "/", "/about", "/about-us", "/community", "/press", "/download", "/pricing",
    "/blog", "/therapists", "/built-in-a-day", "/demo-safety",
    "/login", "/register", "/forgot-password", "/waitlist", "/whats-new",
    "/contact", "/security", "/data-protection", "/learn-and-grow",
    "/ready-boards", "/therapist-portal", "/schools-portal",
    "/privacy", "/terms", "/privacy-promise", "/demo/staged", "/living-studio",
  ]);
  const isPublicMarketingPage =
    PUBLIC_BANNER_PATHS.has(location.pathname) ||
    location.pathname.startsWith("/blog/") ||
    location.pathname.startsWith("/therapists/") ||
    location.pathname.startsWith("/families/");

  return (
    <>
      {/* Public marketing pages get the full site header (contest brief
          2026-07-11); app surfaces keep TopNav. HamburgerMenu is retired —
          PublicHeader carries its own mobile sheet. */}
      {isPublicMarketingPage ? <PublicHeader /> : <TopNav />}
      <ChildAvatarStrip />
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/built-in-a-day" element={<Navigate to="/about" replace />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/consent/parental/verify" element={<ProtectedRoute><ParentalConsentVerify /></ProtectedRoute>} />
      <Route path="/profiles" element={<ProtectedRoute><Profiles /></ProtectedRoute>} />
      <Route path="/pick-board" element={<ProtectedRoute><PickBoard /></ProtectedRoute>} />
      <Route path="/create-board" element={<ProtectedRoute><CreateBoardWizard /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/privacy-promise" element={<PrivacyPromisePage />} />
      <Route path="/demo" element={<DemoBoardPage />} />
      <Route path="/living-studio" element={<LivingTileStudio />} />
      <Route path="/demo/staged" element={<DemoStaged />} />
      <Route path="/my-profile" element={<ProtectedRoute><MyProfile /></ProtectedRoute>} />
      <Route path="/therapist-portal" element={<Professionals />} />
      <Route path="/schools-portal" element={<Professionals />} />
      <Route path="/ready-boards" element={<ReadyBoards />} />
      <Route path="/messages" element={<ProtectedRoute><ComingSoonPage topic="messages" title="Messages — coming soon" blurb="A shared inbox for parents, therapists, and school staff working on the same child's board. Tell us what you'd like first." /></ProtectedRoute>} />
      <Route path="/about-us" element={<AboutUs />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/security" element={<Security />} />
      <Route path="/data-protection" element={<DataProtection />} />
      <Route path="/learn-and-grow" element={<Navigate to="/blog" replace />} />
      <Route path="/clients" element={<ProtectedRoute><TherapistDashboard /></ProtectedRoute>} />
      <Route path="/board/:profileId" element={<ProtectedRoute><Board /></ProtectedRoute>} />
      <Route path="/manage/:profileId" element={<ProtectedRoute><ManageBoard /></ProtectedRoute>} />
      <Route path="/settings/:profileId" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/report/:profileId" element={<ProtectedRoute><Report /></ProtectedRoute>} />
      <Route path="/first-then/:profileId" element={<ProtectedRoute><FirstThen /></ProtectedRoute>} />
      <Route path="/choice/:profileId" element={<ProtectedRoute><ChoiceBoard /></ProtectedRoute>} />
      <Route path="/coach/:profileId" element={<ProtectedRoute><Coach /></ProtectedRoute>} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/about" element={<About />} />
      <Route path="/community" element={<Community />} />
      <Route path="/press" element={<Press />} />
      <Route path="/download" element={<Download />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
      <Route path="/therapists" element={<Therapists />} />
      <Route path="/therapists/:slug" element={<TherapistDetail />} />
      <Route path="/therapist-application" element={<ProtectedRoute><TherapistApplication /></ProtectedRoute>} />
      <Route path="/families/:slug" element={<FamilyProfile />} />
      <Route path="/family-story" element={<ProtectedRoute><FamilyProfileEditor /></ProtectedRoute>} />
      <Route path="/admin/verify" element={<ProtectedRoute><AdminVerify /></ProtectedRoute>} />
      <Route path="/admin/audit-log" element={<ProtectedRoute><AdminAuditLog /></ProtectedRoute>} />
      <Route path="/admin/grant" element={<ProtectedRoute><AdminGrant /></ProtectedRoute>} />
      <Route path="/admin/security" element={<ProtectedRoute><AdminSecurity /></ProtectedRoute>} />
      <Route path="/admin/region" element={<ProtectedRoute><AdminRegion /></ProtectedRoute>} />
      <Route path="/admin/funnel" element={<ProtectedRoute><AdminFunnel /></ProtectedRoute>} />
      <Route path="/admin/doctor-codes" element={<ProtectedRoute><AdminDoctorCodes /></ProtectedRoute>} />
      <Route path="/admin/child-devices" element={<ProtectedRoute><AdminChildDevices /></ProtectedRoute>} />
      <Route path="/admin/media" element={<ProtectedRoute><AdminMedia /></ProtectedRoute>} />
      <Route path="/admin/pilot" element={<ProtectedRoute><AdminPilot /></ProtectedRoute>} />
      <Route path="/admin/pilot/:profileId" element={<ProtectedRoute><AdminPilot /></ProtectedRoute>} />
      <Route path="/pair-device" element={<ProtectedRoute><PairChildDevice /></ProtectedRoute>} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/import/:token" element={<ShareImport />} />
      <Route path="/invite/:code" element={<InviteRedeem />} />
      <Route path="/refer/:code" element={<ReferRedeem />} />
      <Route path="/demo-safety" element={<DemoSafety />} />
      <Route path="/waitlist" element={<Waitlist />} />
      <Route path="/whats-new" element={<WhatsNew />} />
      <Route path="/privacy/me" element={<ProtectedRoute><PrivacyMe /></ProtectedRoute>} />
      <Route path="/helpers/accept/:token" element={<ProtectedRoute><HelperAccept /></ProtectedRoute>} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    {isPublicMarketingPage && <SiteFooter />}
    </>
  );
}

function App() {
  // On a paired child device, skip the global TickerBanner too — it's part
  // of the admin chrome the child should never see. The check is fine to
  // do at this level because changing the marker requires a full reload
  // (initiated either by claim or by the unpair poll).
  const childDevice = typeof window !== "undefined" && isChildDevice();
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          {!childDevice && <TickerBanner />}
          <AppRouter />
          <InstallPrompt />
          <IosInstallCard />
          <TierUpgradeListener />
          <PWAUpdateBanner />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
